import {
  MaestroHistoricTaskProgress,
  RunProgress,
  TaskProgress,
} from '@automata/run-visualizer/src/types';
import {
  PlanTaskDetail,
  WorkflowPlanResult,
} from '@automata/wfb-api/src/apis/wfbApiSchemas';
import { Plan } from '@automata/wfb-api/src/useWorkflowPlan/types';

import { selectWorkflowJsonToPreview } from '../../features/workflowJsonSelectors';
import { HARDCODED_BATCH_NUMBER } from '../../features/workflowJsonSelectors/constants';

const toTitleCase = (input: string): string =>
  `${input.charAt(0).toUpperCase()}${input.slice(1).toLowerCase()}`;

const noLabwareData = [
  {
    label: 'No labware',
    id: 'no_labware',
    batch: HARDCODED_BATCH_NUMBER,
    type: 'unknown',
    description: 'No labware',
  },
];

export const getTaskLabel = (task: PlanTaskDetail): string => {
  const actionTasks = ['transport', 'load', 'unload', 'unload_input'];
  const taskActionLowercase = task.action.toLowerCase();

  if (actionTasks.includes(taskActionLowercase)) {
    const action =
      taskActionLowercase == 'unload_input' ? 'unload' : task.action;
    return toTitleCase(action);
  }

  return task.description;
};

const transformDatum = (
  taskId: string,
  task: PlanTaskDetail
): MaestroHistoricTaskProgress => ({
  duration: {
    end: task.end,
    start: task.start,
  },
  status: 'succeeded',
  task: {
    id: taskId,
    action: task.action,
    label: getTaskLabel(task),
    batch: task.batch,
    instrument: {
      id: task.instrument.id,
      name: task.instrument.name,
      driver: 'unknown',
      type: 'unknown',
    },
    source_task_id: 'unknown',
    destination_task_id: 'unknown',
    is_manual_interaction: task.is_manual_interaction,
    labware_in:
      task.labware_in.length > 0
        ? task.labware_in.map((labware) => ({
            id: labware.id,
            batch: labware.batch ?? HARDCODED_BATCH_NUMBER,
            type: 'unknown',
            label: labware.description,
            description: labware.description,
          }))
        : noLabwareData,
    labware_out:
      task.labware_out.length > 0
        ? task.labware_out.map((labware) => ({
            id: labware.id,
            batch: labware.batch ?? HARDCODED_BATCH_NUMBER,
            type: 'unknown',
            label: labware.description,
            description: labware.description,
          }))
        : noLabwareData,
  },
});

export const transformData = (input: WorkflowPlanResult): RunProgress => {
  const tasks = Object.entries(input.plan).map(([taskId, task]) =>
    transformDatum(taskId, task)
  );

  // Sort tasks by duration start time, with 'no_labware' tasks first
  const sortedTasks = tasks.toSorted((a, b) => {
    const getLabwareId = (t: TaskProgress) =>
      'labware_in' in t.task ? t.task.labware_in?.[0]?.id : null;

    const aIsNoLabware = getLabwareId(a) === 'no_labware';
    const bIsNoLabware = getLabwareId(b) === 'no_labware';

    if (aIsNoLabware && !bIsNoLabware) return -1;
    if (!aIsNoLabware && bIsNoLabware) return 1;

    return a.duration.start - b.duration.start;
  });

  return {
    pauses: {
      is_currently_paused: false,
      pause_attempts: [],
      total_time_paused_secs: 0,
    },
    batch_progress: [],
    total_estimated_time: input.metrics.total_plan_duration,
    task_progress: sortedTasks,
  };
};

export const getPreviewState = (
  workflowJsonPreview: ReturnType<typeof selectWorkflowJsonToPreview>,
  plan: Plan
): 'blocked' | 'empty' | 'loading' | 'ready' | 'cancelled' => {
  if (plan === undefined && workflowJsonPreview.isError) return 'blocked';
  if (plan === undefined && !workflowJsonPreview.isError) return 'empty';
  if (plan?.status === 'PENDING') return 'loading';
  if (plan?.status === 'CANCELLED' && !plan.result_available)
    return 'cancelled';
  return 'ready';
};

// TODO(LT, 16/07/2024): Move to packages/utils
export const isNotNullish = <T extends unknown>(
  value: T
): value is NonNullable<T> => value !== null && value !== undefined;
